import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  CheckCircle, 
  Trash2, 
  Clock, 
  CreditCard,
  AlertCircle,
  Building2,
  User,
  Mail,
  FileText,
  Calendar,
  Shield
} from 'lucide-react';

const AdminSubmissionDetail = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchSubmission();
  }, [submissionId, user]);

  const fetchSubmission = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/submissions/${submissionId}`);
      setSubmission(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else if (error.response?.status === 404) {
        toast.error('Submission not found');
        navigate('/admin');
      } else {
        toast.error('Failed to load submission details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    try {
      setProcessing(true);
      await api.post(`/admin/submissions/${submissionId}/mark-paid`);
      toast.success('Invoice marked as paid');
      fetchSubmission();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    try {
      setProcessing(true);
      await api.delete(`/admin/submissions/${submissionId}`);
      toast.success('Submission deleted');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete submission');
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOverallRatingBadge = (rating) => {
    if (!rating) return null;
    const colors = {
      green: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      amber: 'bg-amber-100 text-amber-800 border-amber-200',
      red: 'bg-red-100 text-red-800 border-red-200'
    };
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${colors[rating] || colors.amber}`}>
        {rating.charAt(0).toUpperCase() + rating.slice(1)}
      </span>
    );
  };

  if (!user?.is_admin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Submission Not Found</h2>
          <Button onClick={() => navigate('/admin')}>Back to Admin</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="mb-4 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin Hub
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">{submission.venue?.name}</h1>
              <p className="text-muted-foreground font-mono text-sm">{submissionId}</p>
            </div>
            <div className="flex items-center gap-2">
              {submission.payment_status === 'pending_invoice' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
                  <Clock className="w-4 h-4 mr-1" />
                  Pending Invoice
                </span>
              )}
              {(submission.payment_status === 'paid' || submission.payment_status === 'succeeded') && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Paid
                </span>
              )}
              {submission.is_locked && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  <Shield className="w-4 h-4 mr-1" />
                  Locked
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-6">
          {/* Venue Info */}
          <div className="bg-card rounded-xl border p-6">
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Venue Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">Venue Name</div>
                <div className="font-medium">{submission.venue?.name || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Venue Type</div>
                <div className="font-medium">{submission.venue?.venue_type || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Environment</div>
                <div className="font-medium">{submission.venue?.primary_environment || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Areas Assessed</div>
                <div className="font-medium">{submission.areas_count || 0}</div>
              </div>
            </div>
          </div>

          {/* Submitted By */}
          <div className="bg-card rounded-xl border p-6">
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Submitted By
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">Name</div>
                <div className="font-medium">{submission.submitted_by?.name || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Email</div>
                <div className="font-medium">{submission.submitted_by?.email || '-'}</div>
              </div>
            </div>
          </div>

          {/* Invoice Details */}
          {submission.invoice_details && (
            <div className="bg-card rounded-xl border p-6">
              <h2 className="font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Invoice Details
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Organisation Name</div>
                  <div className="font-medium">{submission.invoice_details?.organisation_name || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Contact Email</div>
                  <div className="font-medium">{submission.invoice_details?.contact_email || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">PO Number</div>
                  <div className="font-medium">{submission.invoice_details?.purchase_order_number || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Requested At</div>
                  <div className="font-medium">{formatDate(submission.invoice_details?.requested_at)}</div>
                </div>
              </div>
            </div>
          )}

          {/* Status & Assessment */}
          <div className="bg-card rounded-xl border p-6">
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Status & Assessment
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">Payment Status</div>
                <div className="font-medium capitalize">{submission.payment_status || 'Not paid'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Report Available</div>
                <div className="font-medium">{submission.report_available ? 'Yes' : 'No'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Created</div>
                <div className="font-medium">{formatDate(submission.created_at)}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Locked At</div>
                <div className="font-medium">{formatDate(submission.locked_at)}</div>
              </div>
              {submission.payment_date && (
                <div>
                  <div className="text-sm text-muted-foreground">Payment Date</div>
                  <div className="font-medium">{formatDate(submission.payment_date)}</div>
                </div>
              )}
              <div>
                <div className="text-sm text-muted-foreground">Categories Completed</div>
                <div className="font-medium">{submission.categories_completed || 0} / 7</div>
              </div>
            </div>
            
            {submission.overall_rating && (
              <div className="mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground mb-2">Overall Sensory Rating</div>
                {getOverallRatingBadge(submission.overall_rating)}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="bg-card rounded-xl border p-6">
            <h2 className="font-semibold mb-4">Actions</h2>
            <div className="flex flex-wrap gap-3">
              {submission.report_available && (
                <Button 
                  variant="outline"
                  onClick={() => window.open(`/submission/${submissionId}/summary`, '_blank')}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View Report
                </Button>
              )}
              
              {submission.payment_status === 'pending_invoice' && (
                <Button 
                  onClick={handleMarkPaid}
                  disabled={processing}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {processing ? 'Processing...' : 'Mark as Paid'}
                </Button>
              )}
              
              <Button 
                variant="destructive"
                onClick={() => setDeleteConfirm(true)}
                disabled={processing}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Submission
              </Button>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Delete Submission?</h3>
                  <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Are you sure you want to delete this submission for <strong>{submission.venue?.name}</strong>? 
                All associated data will be permanently removed.
              </p>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setDeleteConfirm(false)} disabled={processing}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleDelete}
                  disabled={processing}
                >
                  {processing ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSubmissionDetail;
